// Core
import React from 'react'
import clsx from 'clsx'

// Hooks
import _useDidMount from 'hooks/lifecycle/use-did-mount'
import { _useGlobalDemo } from 'hooks/use-demo'

// Redux
import { useAppDispatch, useAppSelector } from 'state-manager/store'
import { useHistory } from 'react-router-dom'

// Assets
import { ReactComponent as FBIcon } from 'assets/icons/facebook_icon.svg'
import { ReactComponent as GoogleIcon } from 'assets/icons/googleads_icon_circle.svg'
import { ReactComponent as LinkedInIcon } from 'assets/icons/linkedin_icon_circle.svg'
import { ReactComponent as MicrosoftIcon } from 'assets/icons/microsoft_icon_circle.svg'
import { ReactComponent as ButtonCheck } from 'assets/icons/button_check.svg'

// Components
import Button from 'components/Onboarding/Button/Button'

// Styles
import classes from 'components/Onboarding/ConnectDataSources/ConnectDataSources.module.scss'

// Selectors
import { showGlobalModal } from 'state-manager/actions/global-modal'

// Utils
import notification from 'utils/notifications'
import {
  fetchAllDataSources,
  fetchDataSourceLink,
} from 'utils/requests'
import { sendGTMEvent } from 'utils/gtm/sendGTMEvent'

// Constants
import {
  DATASOURCE_FACEBOOK,
  DATASOURCE_GOOGLE,
  DATASOURCE_LINKEDIN,
  DATASOURCE_MICROSOFT,
} from 'constants/data-sources'

// Routes
import routes from 'routes'

// Types
import { IDataSourceItem } from 'state-manager/reducers/data-sources'

const integrations = [
  {
    id: DATASOURCE_GOOGLE,
    icon: <GoogleIcon />,
    name: 'Google Ads',
    text: 'Optimize your search, display, video, shopping and other campaigns',
  },
  {
    id: DATASOURCE_MICROSOFT,
    icon: <MicrosoftIcon />,
    name: 'Microsoft Ads',
    text: 'Optimize your Microsoft Advertising campaigns.',
  },
  {
    id: DATASOURCE_FACEBOOK,
    icon: <FBIcon />,
    name: 'Facebook Ads',
    text: 'Optimize your Facebook and Instagram campaigns.',
  },
  {
    id: DATASOURCE_LINKEDIN,
    icon: <LinkedInIcon />,
    name: 'LinkedIn Ads',
    text: 'Optimize your professional ad campaigns on LinkedIn.',
  },
]

const getWindowDimensions = () => {
  const w = Number(window.screen.width / 2)
  const h = Number(window.screen.height / 2)
  const tops = Number(window.screen.height / 2 - h / 2)
  const left = Number(window.screen.width / 2 - w / 2)

  return { w, h, tops, left }
}

const generateDataSourceLink = async (datasource: string) => {
  const res = await fetchDataSourceLink(datasource)
  return res.data.oauthLink
}

const openDataSourceLink = (dataSourceLink: string | null) => {
  if (dataSourceLink) {
    const { w, h, tops, left } = getWindowDimensions()
    window.open(
      dataSourceLink,
      '_blank',
      `toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=no, resizable=no, copyhistory=no, width=${w}, height=${h}, top=${tops}, left=${left}`
    )
  }
}

const dataSourceIsConnected = (
  dataSource: string,
  fetchedDataSources: IDataSourceItem[]
) => {
  return fetchedDataSources.some(
    (dataSourceItem) =>
      dataSourceItem.type === dataSource && dataSourceItem.isConnected
  )
}

export default function ConnectDataSources() {
  const history = useHistory()
  const demoHOC = _useGlobalDemo()

  const { data: dataSources } = useAppSelector((state) => state.dataSources)
  const [udCompany] = useAppSelector(
    (state) => [state.userData.company]
  )

  const dispatch = useAppDispatch()

  const handleConnectClick = async (integrationId: string) => {
    const dataSourceLink = await generateDataSourceLink(integrationId)

    openDataSourceLink(dataSourceLink)
  }

  const hasConnectedDataSource = dataSources.some(
    (dataSource) => dataSource.isConnected
  )

  _useDidMount(() => {
    fetchAllDataSources(dispatch)
  })

  window.onmessage = (e) => {
    if (e.data.message) {
      if (parseInt(e.data.error) && e.data.provider === DATASOURCE_GOOGLE) {
        dispatch(
          showGlobalModal({
            type: 'error',
            header: 'Error!',
            description:
              'Please grant all requested permissions, including access to Google Ads accounts and data.',
            btnText: 'Try again',
            btnHandler: async () => {
              const googleLink = await generateDataSourceLink(DATASOURCE_GOOGLE)
              openDataSourceLink(googleLink)
            },
          })
        )
        return
      }

      if (parseInt(e.data.error)) {
        notification.error(e.data.message)
        return
      }

      sendGTMEvent({
        event: 'connection',
        network: e.data.provider,
        businessType: udCompany.businessType
      })

      notification.success(
        'Please note it can take up to 15 minutes to gather the advertising data for newly added sources. You may close this window.'
      )
      fetchAllDataSources(dispatch)
    }
  }

  return (
    <>
      {integrations.map((integration) => {
        const isConnected = dataSourceIsConnected(integration.id, dataSources)

        return (
          <div key={integration.id} className={clsx(classes.container)}>
            <div className={clsx(classes.contentContainer)}>
              {integration.icon}
              <div>
                <h5 className={clsx(classes.name)}>{integration.name}</h5>
                <p className={clsx(classes.text)}>{integration.text}</p>
              </div>
            </div>
            <div className={clsx(classes.buttonContainer)}>
              <Button
                className={clsx(
                  classes.integrationButton,
                  isConnected && classes.integrationSuccessButton
                )}
                onClick={demoHOC(() => handleConnectClick(integration.id))}
                title={isConnected ? 'Success' : 'Connect'}
                dataCy={isConnected ? 'success' : 'connect'}
                icon={isConnected ? <ButtonCheck /> : null}
              />
            </div>
          </div>
        )
      })}
      <div className={clsx('mt-4')}>
        <Button
          inverted
          title="Skip"
          dataCy="skip"
          className={clsx(classes.skipButton)}
          onClick={() => {
            history.push(routes.dashboardClients)
          }}
        />
        {hasConnectedDataSource && (
          <Button
            title="Continue"
            dataCy="continue"
            className={clsx(classes.continueButton)}
            onClick={() => {
              history.push(routes.dashboardClients) // change to forward route
            }}
          />
        )}
      </div>
    </>
  )
}
